import React, { useState, useEffect } from 'react';
import { useLocation as useAppLocation } from '../../contexts/LocationContext';
import { ConsumptionBasedGHGInventoryWithMetrics } from '../charts/ConsumptionBasedGHGInventoryWithMetrics';
import { SectorBasedGHGInventoryWithMetrics } from '../charts/SectorBasedGHGInventoryWithMetrics';
import { InventoryComparisonChart } from '../charts/InventoryComparisonChart';
import { ChartLayout, EquityContent, MethodologyContent, OverviewContent, RelevanceContent } from '../charts/ChartLayout';
import { KeyMetric, KeyMetrics } from '../charts/KeyMetrics';
import { PageHeader } from '../layout/PageHeader';
import { fetchInventoryComparisonData } from '../../services/inventoryComparisonService';
import GenericMapTiered from '../charts/GenericMapTiered';

interface GHGInventoryProps {
  location?: string;
}

export const GHGInventory: React.FC<GHGInventoryProps> = ({ location: propLocation }) => {
  const { selectedLocation: ctxLocation, coreDataLoading } = useAppLocation();
  const [selectedLocation] = useState<string>('');
  const [inventoryComparisonData, setInventoryComparisonData] = useState<any>(null);
  const [inventoryComparisonLoading, setInventoryComparisonLoading] = useState(true);
  const [inventoryComparisonActiveView, setInventoryComparisonActiveView] = useState<string>('overview');
  const [mapActiveView, setMapActiveView] = useState<string>('overview');
  const [carbonFootprintMetrics, setCarbonFootprintMetrics] = useState<{
    currentValue: number;
    californiaAverage: number;
    year: number;
    variable: string;
  } | null>(null);

  const currentLocation = propLocation || ctxLocation || selectedLocation;

  // Load inventory comparison data
  useEffect(() => {
    const loadInventoryComparisonData = async () => {
      if (!currentLocation) return;
      
      try {
        setInventoryComparisonLoading(true);
        const result = await fetchInventoryComparisonData(currentLocation);
        setInventoryComparisonData(result);
      } catch (error) {
        console.error('Error loading inventory comparison data:', error);
      } finally {
        setInventoryComparisonLoading(false);
      }
    };

    loadInventoryComparisonData();
  }, [currentLocation]);

  if (!currentLocation) {
    return (
      <div>
        <PageHeader 
          title="GHG Inventory" 
          subtitle="Please select a location to view greenhouse gas inventory data."
        />
      </div>
    );
  }

  if (coreDataLoading) {
    return (
      <div>
        <PageHeader 
          title={"Loading core data..."}
          subtitle={"Preloading VMT, electricity, and natural gas data for this location."}
        />
      </div>
    );
  }

  return (
    <div>
      <PageHeader 
        title={`GHG Inventory - ${currentLocation}`}
        subtitle="Comprehensive greenhouse gas emissions tracking and analysis for the selected location."
        variant="ghg"
      />

      {/* Sector-Based GHG Inventory Section */}
      <SectorBasedGHGInventoryWithMetrics location={currentLocation} />

      {/* Consumption-Based GHG Inventory Section */}
      <ConsumptionBasedGHGInventoryWithMetrics location={currentLocation} />

      {/* Inventory Comparison Section */}
      <ChartLayout
        title="Inventory Comparison"
        description="Comparison between sector-based and consumption-based GHG inventory approaches."
        chartType="line"
        keyMetrics={
          inventoryComparisonLoading ? (
            <KeyMetrics>
              <KeyMetric 
                value="Loading..." 
                label="Sector-Based Total (MT CO₂e)"
                color="#6b7280"
              />
              <KeyMetric 
                value="Loading..." 
                label="Consumption-Based Total (MT CO₂e)"
                color="#6b7280"
              />
              <KeyMetric 
                value="Loading..." 
                label="Difference (%)"
                color="#6b7280"
              />
            </KeyMetrics>
          ) : inventoryComparisonData ? (
            <KeyMetrics>
              <KeyMetric 
                value={`${inventoryComparisonData.sectorBasedTotal.toLocaleString()}`}
                label="Sector-Based Total (MT CO₂e)"
                color="#3b82f6"
              />
              <KeyMetric 
                value={`${inventoryComparisonData.consumptionBasedTotal.toLocaleString()}`}
                label="Consumption-Based Total (MT CO₂e)"
                color="#10b981"
              />
              <KeyMetric 
                value={`${inventoryComparisonData.differencePercent >= 0 ? '+' : ''}${inventoryComparisonData.differencePercent.toFixed(1)}%`}
                label="Difference (%)"
                color={inventoryComparisonData.differencePercent >= 0 ? "#f59e0b" : "#ef4444"}
              />
            </KeyMetrics>
          ) : (
            <KeyMetrics>
              <KeyMetric 
                value="No data" 
                label="Sector-Based Total (MT CO₂e)"
                color="#6b7280"
              />
              <KeyMetric 
                value="No data" 
                label="Consumption-Based Total (MT CO₂e)"
                color="#6b7280"
              />
              <KeyMetric 
                value="No data" 
                label="Difference (%)"
                color="#6b7280"
              />
            </KeyMetrics>
          )
        }
        chartContent={<InventoryComparisonChart location={currentLocation} />}
        overviewContent={
          <OverviewContent
            title="Inventory Comparison Overview"
            description="This chart compares sector-based and consumption-based GHG inventory totals over time to highlight the differences between these two accounting approaches."
          >
            <p>
              This time series comparison between sector-based and consumption-based inventories reveals important insights about how emissions are measured and attributed over time. 
              Sector-based inventories track emissions by economic sectors (transportation, buildings, industry, etc.) within the community's geographic boundaries. 
              Consumption-based inventories allocate emissions to final consumers based on their consumption patterns and lifestyle choices, regardless of where the emissions physically occur.
            </p>
            <p>
              By comparing these approaches over time, communities can see how the gap between production-side and consumption-driven emissions evolves, 
              helping them develop more comprehensive climate action strategies that address both types of emissions patterns.
            </p>
          </OverviewContent>
        }
        relevanceContent={
          <RelevanceContent
            title="Relevance to Climate Action"
            description="Comparing inventory approaches helps communities understand the full scope of their climate impact and develop more effective strategies."
          >
            <div className="methodology-details-simple">
              <h5>Comprehensive Planning</h5>
              <p>Understanding both approaches enables communities to develop strategies that address emissions from multiple angles.</p>
            </div>
            <div className="methodology-details-simple">
              <h5>Policy Development</h5>
              <p>Different inventory approaches may require different policy interventions and behavioral change strategies.</p>
            </div>
            <div className="methodology-details-simple">
              <h5>Stakeholder Engagement</h5>
              <p>Both approaches engage different stakeholders - sector-based focuses on businesses and infrastructure, consumption-based on residents and lifestyle choices.</p>
            </div>
          </RelevanceContent>
        }
        equityContent={
          <EquityContent
            title="Equity Considerations"
            description="Different inventory approaches may reveal different equity implications and require different approaches to ensure equitable climate action."
          >
            <div className="methodology-details-simple">
              <h5>Consumption Patterns</h5>
              <p>Consumption-based inventories may reveal disparities in emissions based on income, access to alternatives, and lifestyle choices.</p>
            </div>
            <div className="methodology-details-simple">
              <h5>Geographic Boundaries</h5>
              <p>Sector-based inventories focus on local emissions, while consumption-based inventories consider global supply chains and their equity implications.</p>
            </div>
            <div className="methodology-details-simple">
              <h5>Policy Impact</h5>
              <p>Different approaches may affect different communities differently, requiring careful consideration of equity in policy design.</p>
            </div>
          </EquityContent>
        }
        methodologyContent={
          <MethodologyContent
            title="Methodology"
            description="The comparison uses the same base year and geographic boundaries to ensure meaningful comparison between inventory approaches."
            dataSources={[
              'Sector-based inventory data from local emissions tracking',
              'Consumption-based inventory data from CBEI model',
              'Population and household data for scaling',
              'Economic and consumption pattern data'
            ]}
            methodology={[
              'Both inventories use the same base year and geographic boundaries for comparison.',
              'Sector-based totals sum all emissions from economic sectors within the community.',
              'Consumption-based totals sum emissions allocated to community residents based on consumption patterns.',
              'Values are converted to consistent units (MT CO₂e) and scaled to community level.',
              'Differences are calculated as absolute values and percentages.',
              'Comparison helps identify gaps and overlaps between the two approaches.',
              'Limitations: Different methodologies may not be directly comparable due to different accounting principles.'
            ]}
          />
        }
        activeView={inventoryComparisonActiveView}
        onViewChange={setInventoryComparisonActiveView}
        showRelevanceEquity={true}
        enableComments={true}
        location={currentLocation}
      />

      {/* Average Household Carbon Footprint Map */}
      <ChartLayout
        title="Average Household Carbon Footprint Map"
        description="Interactive map showing household carbon footprints by census tract for the selected location."
        chartType="map"
        layout="vertical"
        keyMetrics={
          carbonFootprintMetrics ? (
            <KeyMetrics>
              <KeyMetric
                value={`${carbonFootprintMetrics.currentValue.toFixed(1)} MT CO₂e/HH`}
                label="Average Household Carbon Footprint"
              />
              <KeyMetric
                value={`${carbonFootprintMetrics.californiaAverage.toFixed(1)} MT CO₂e/HH`}
                label="CA Average"
              />
            </KeyMetrics>
          ) : undefined
        }
        chartContent={
          <GenericMapTiered
            location={currentLocation}
            hideVariableSelector={true}
            enableScopeSelector={true}
            onDataLoaded={(metrics) => {
              setCarbonFootprintMetrics(metrics);
            }}
          />
        }
        overviewContent={
          <OverviewContent
            title="Carbon Footprint Map Overview"
            description="This interactive map displays average household carbon footprints by census tract, providing detailed insights into consumption-based emissions patterns across the community."
          >
            <p>
              The map shows carbon footprints calculated using the Consumption-Based Emissions Inventory (CBEI) methodology, 
              which accounts for emissions from transportation, housing, food, goods, and services consumption patterns. 
              Each census tract is color-coded based on the average household carbon footprint, allowing for detailed 
              geographic analysis of emissions patterns within the community.
            </p>
            <p>
              Click on any tract to view detailed breakdowns of emissions by category, including transportation, 
              housing, food, goods, and services. This granular view helps identify areas with higher or lower 
              carbon footprints and can inform targeted climate action strategies.
            </p>
          </OverviewContent>
        }
        relevanceContent={
          <RelevanceContent
            title="Relevance to Climate Action"
            description="Carbon footprint mapping helps communities identify priority areas for emissions reduction and develop targeted interventions."
          >
            <div className="methodology-details-simple">
              <h5>Targeted Interventions</h5>
              <p>High-footprint areas can be prioritized for specific programs like energy efficiency, transportation alternatives, or sustainable consumption initiatives.</p>
            </div>
            <div className="methodology-details-simple">
              <h5>Equity Considerations</h5>
              <p>Mapping reveals disparities in carbon footprints that may correlate with income, access to alternatives, or other demographic factors.</p>
            </div>
            <div className="methodology-details-simple">
              <h5>Policy Development</h5>
              <p>Geographic patterns inform zoning policies, infrastructure investments, and community-based climate programs.</p>
            </div>
          </RelevanceContent>
        }
        equityContent={
          <EquityContent
            title="Equity Considerations"
            description="Carbon footprint mapping reveals important equity implications for climate action planning."
          >
            <div className="methodology-details-simple">
              <h5>Access to Alternatives</h5>
              <p>Areas with limited access to public transit, energy-efficient housing, or sustainable food options may show higher carbon footprints.</p>
            </div>
            <div className="methodology-details-simple">
              <h5>Economic Factors</h5>
              <p>Income levels often correlate with consumption patterns and access to low-carbon alternatives, affecting household carbon footprints.</p>
            </div>
            <div className="methodology-details-simple">
              <h5>Community Resources</h5>
              <p>Investment in community resources, infrastructure, and services can help reduce carbon footprints while improving quality of life.</p>
            </div>
          </EquityContent>
        }
        methodologyContent={
          <MethodologyContent
            title="Methodology"
            description="Carbon footprint data is calculated using the Consumption-Based Emissions Inventory (CBEI) methodology developed by EcoDataLab."
            dataSources={[
              'EcoDataLab Consumption-Based Emissions Inventory (CBEI)',
              'Census tract-level demographic and economic data',
              'Household consumption pattern surveys',
              'Regional economic input-output models',
              'Transportation and energy use data'
            ]}
            methodology={[
              'Carbon footprints calculated using consumption-based accounting methodology.',
              'Emissions allocated to households based on consumption patterns and lifestyle choices.',
              'Data includes transportation, housing, food, goods, and services consumption.',
              'Values converted from grams to metric tons CO₂ equivalent and rounded to one decimal place.',
              'Map displays average household carbon footprint by census tract for 2023.',
              'Color coding based on decile distribution of carbon footprint values.',
              'Limitations: Data represents 2023 estimates and may not reflect recent changes in consumption patterns.'
            ]}
          />
        }
        activeView={mapActiveView}
        onViewChange={setMapActiveView}
        showRelevanceEquity={true}
        enableComments={true}
        location={currentLocation}
      />
    </div>
  );
};
